import React from 'react';
import diagnosisAyurvedicMask from '@/assets/images/diagnosis-ayurvedic.png';
import type { ClinicalAssessmentEntry } from '@/ayurveda/types/ayurveda';

export interface DiagnosisTilesProps {
  entries: ClinicalAssessmentEntry[];
}

/** Exact Figma icon for the Ayurvedic diagnosis tile (herbal bowl), tinted via CSS mask. */
function AyurvedicDiagnosisIcon() {
  return (
    <span
      className="block h-[15px] w-[15px] bg-[#8AA165]"
      style={{
        WebkitMaskImage: `url(${diagnosisAyurvedicMask})`,
        maskImage: `url(${diagnosisAyurvedicMask})`,
        WebkitMaskSize: 'contain',
        maskSize: 'contain',
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
        maskPosition: 'center',
      }}
      aria-hidden="true"
    />
  );
}

/** Exact Figma icon for the Allopathic diagnosis tile (medical bag). */
function AllopathicDiagnosisIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true">
      <path
        d="M12.6562 3.28125H10.3125V2.8125C10.3125 2.43954 10.1643 2.08185 9.90062 1.81813C9.6369 1.55441 9.27921 1.40625 8.90625 1.40625H6.09375C5.72079 1.40625 5.3631 1.55441 5.09938 1.81813C4.83566 2.08185 4.6875 2.43954 4.6875 2.8125V3.28125H2.34375C2.09511 3.28125 1.85665 3.38002 1.68084 3.55584C1.50502 3.73165 1.40625 3.97011 1.40625 4.21875V11.7188C1.40625 11.9674 1.50502 12.2058 1.68084 12.3817C1.85665 12.5575 2.09511 12.6562 2.34375 12.6562H12.6562C12.9049 12.6562 13.1433 12.5575 13.3192 12.3817C13.495 12.2058 13.5938 11.9674 13.5938 11.7188V4.21875C13.5938 3.97011 13.495 3.73165 13.3192 3.55584C13.1433 3.38002 12.9049 3.28125 12.6562 3.28125ZM8.90625 8.4375H7.96875V9.375C7.96875 9.49932 7.91936 9.61855 7.83146 9.70646C7.74355 9.79436 7.62432 9.84375 7.5 9.84375C7.37568 9.84375 7.25645 9.79436 7.16854 9.70646C7.08064 9.61855 7.03125 9.49932 7.03125 9.375V8.4375H6.09375C5.96943 8.4375 5.8502 8.38811 5.76229 8.30021C5.67439 8.2123 5.625 8.09307 5.625 7.96875C5.625 7.84443 5.67439 7.7252 5.76229 7.63729C5.8502 7.54939 5.96943 7.5 6.09375 7.5H7.03125V6.5625C7.03125 6.43818 7.08064 6.31895 7.16854 6.23104C7.25645 6.14314 7.37568 6.09375 7.5 6.09375C7.62432 6.09375 7.74355 6.14314 7.83146 6.23104C7.91936 6.31895 7.96875 6.43818 7.96875 6.5625V7.5H8.90625C9.03057 7.5 9.1498 7.54939 9.23771 7.63729C9.32561 7.7252 9.375 7.84443 9.375 7.96875C9.375 8.09307 9.32561 8.2123 9.23771 8.30021C9.1498 8.38811 9.03057 8.4375 8.90625 8.4375ZM9.375 3.28125H5.625V2.8125C5.625 2.68818 5.67439 2.56895 5.76229 2.48104C5.8502 2.39314 5.96943 2.34375 6.09375 2.34375H8.90625C9.03057 2.34375 9.1498 2.39314 9.23771 2.48104C9.32561 2.56895 9.375 2.68818 9.375 2.8125V3.28125Z"
        fill="#8AA165"
      />
    </svg>
  );
}

function DiagnosisTile({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value?: string | null;
}) {
  return (
    <div className="flex h-full min-w-0 flex-col items-center justify-center gap-2.5 rounded-2xl bg-[#F8F8F1] px-4 py-1">
      <div className="flex items-center justify-center gap-1.5">
        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-white text-[#8AA165]">
          {icon}
        </div>
        <span className="font-hanken text-base font-medium text-[#42434B]">{label}:</span>
      </div>
      <p
        className={
          value
            ? 'break-words text-center font-hanken text-sm text-[#585859]'
            : 'break-words text-center font-hanken text-sm italic text-muted-foreground'
        }
      >
        {value || 'No data available'}
      </p>
    </div>
  );
}

/** Ayurvedic/Allopathic diagnosis tile row (no outer card — used inside AITranscribedDataCard). */
export function DiagnosisTiles({ entries }: DiagnosisTilesProps) {
  const ayurvedic = entries.find((e) => e.parameter === 'diagnosis_ayurvedic')?.value;
  const allopathic = entries.find((e) => e.parameter === 'diagnosis_allopathic')?.value;

  return (
    <div className="grid grid-cols-2 items-stretch gap-2">
      <DiagnosisTile icon={<AyurvedicDiagnosisIcon />} label="Ayurvedic" value={ayurvedic} />
      <DiagnosisTile icon={<AllopathicDiagnosisIcon />} label="Allopathic" value={allopathic} />
    </div>
  );
}
